import { forwardRef } from 'react';
import { useCurrency } from '@/hooks/useCurrency';

interface PriceDisplayProps {
  amount: number;
  packagingOptionId?: string;
  className?: string;
  showSecondary?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const PriceDisplay = forwardRef<HTMLDivElement, PriceDisplayProps>(({ 
  amount, 
  packagingOptionId,
  className = '', 
  showSecondary = true,
  size = 'md' 
}, ref) => {
  const { formatPrice, formatProductPrice } = useCurrency();
  
  // Use product-specific pricing if packagingOptionId is provided, otherwise fall back to generic formatting
  const { primary, secondary } = packagingOptionId 
    ? formatProductPrice(packagingOptionId, amount)
    : formatPrice(amount);

  const sizeClasses = {
    sm: {
      primary: 'text-sm font-medium',
      secondary: 'text-xs',
    },
    md: {
      primary: 'text-base font-semibold',
      secondary: 'text-sm',
    },
    lg: {
      primary: 'text-lg font-bold',
      secondary: 'text-base',
    },
  };

  return (
    <div ref={ref} className={`flex flex-col ${className}`}>
      <span className={sizeClasses[size].primary}>{primary}</span>
      {showSecondary && secondary && (
        <span className={`${sizeClasses[size].secondary} text-muted-foreground opacity-80`}>
          {secondary}
        </span>
      )}
    </div>
  );
});

PriceDisplay.displayName = 'PriceDisplay';

export default PriceDisplay;
